import { ChevronRightIcon, HomeIcon } from "components/icons/Icons";
import { useTranslation } from "react-i18next";
import { NsKey } from "enums/NsKey";
import { LightRealEstateResult } from "./RealEstateSearchBar";
import { ReactNode, useCallback } from "react";
import { CommercialIcon } from "components/icons/customIcons";

type SearchBarSize = 'sm' | 'md' | 'lg';

type RealEstateSearchBarItemProps = {
  property: LightRealEstateResult;
  selected: boolean;
  size?: SearchBarSize;
}

const ICON_CLASS = "w-6 text-primary";

export const RealEstateSearchBarItem: React.FC<RealEstateSearchBarItemProps> = ({
  property,
  selected,
  size = 'lg'
}) => {
  const { t } = useTranslation(NsKey.RealEstate);

  const getPropertyIcon = useCallback((property: LightRealEstateResult): ReactNode => {
    if (property.isProfessionalUse) {
      return <CommercialIcon className={ICON_CLASS} />;
    }

    // Vous pouvez adapter cette logique selon vos types de propriétés
    const propertyTypeLower = property.propertyType?.toLowerCase();
    if (propertyTypeLower?.includes('industrial') || propertyTypeLower?.includes('industri')) {
      return <CommercialIcon className={ICON_CLASS} />;
    }

    return <HomeIcon className={ICON_CLASS} />;
  }, []);

  // Helper function pour formater la surface
  const formatArea = (area?: number): string => {
    if (!area) return "";
    return `${area} m²`;
  };

  // Helper function pour formater le nombre de pièces
  const formatRoomCount = (roomCount?: number): string => {
    if (!roomCount) return "";
    return roomCount === 1 ?
      t("REAL_ESTATE_FORM.LABELS.ONE_ROOM") :
      t("REAL_ESTATE_FORM.LABELS.ROOMS_COUNT", { count: roomCount });
  };

  // Helper function pour formater les charges
  const formatCharges = (charges?: number): string => {
    if (!charges) return "";
    return t("REAL_ESTATE_FORM.LABELS.CHARGES_AMOUNT", { amount: charges.toLocaleString() });
  };

  // Helper function pour les indicateurs de statut
  const getStatusIndicators = (property: LightRealEstateResult): string[] => {
    const indicators: string[] = [];

    if (property.isProfessionalUse) {
      indicators.push(t("REAL_ESTATE_FORM.LABELS.PROFESSIONAL_USE"));
    }

    if (property.isOccupied) {
      indicators.push(t("REAL_ESTATE_FORM.LABELS.OCCUPIED"));
    }

    return indicators;
  };

  // Version simplifiée pour sm et md - seulement icône et nom
  if (size === 'sm' || size === 'md') {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 w-full min-w-0">
          <div className="flex-shrink-0">
            {getPropertyIcon(property)}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`${size === 'sm' ? 'text-xs' : 'text-sm'} font-medium truncate ${selected ? "font-semibold" : ""}`}>
              {property.name}
            </p>
            {/* Affichage des infos de base pour les petites tailles */}
            <div className={`${size === 'sm' ? 'text-xs' : 'text-xs'} text-gray-500 truncate flex space-x-2`}>
              {property.area && <span>{formatArea(property.area)}</span>}
              {property.roomCount && property.area && <span>•</span>}
              {property.roomCount && <span>{formatRoomCount(property.roomCount)}</span>}
            </div>
          </div>
        </div>
        <ChevronRightIcon
          className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} text-gray-400 group-data-[focus]:text-primary flex-shrink-0 ml-2`}
        />
      </div>
    );
  }

  // Version complète pour lg
  const statusIndicators = getStatusIndicators(property);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3 w-full">
        <div className="flex-shrink-0">
          {getPropertyIcon(property)}
        </div>
        <div className="flex grow text-left">
          <div className="flex justify-between items-center grow space-x-8 mr-4">
            {/* Nom et adresse */}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${selected ? "font-semibold" : ""}`}>
                {property.name}
              </p>
              {property.address && (
                <p className="text-xs text-gray-500">
                  {property.address.line1}
                  {property.address.line2 && `, ${property.address.line2}`}
                  {property.address.zipCode && `, ${property.address.zipCode}`}
                  {property.address.city && ` ${property.address.city}`}
                </p>
              )}
            </div>

            {/* Caractéristiques du bien */}
            <div className="flex flex-col items-end min-w-0">
              <div className="flex items-center space-x-3 text-xs text-gray-500">
                {property.area && (
                  <span className="whitespace-nowrap">{formatArea(property.area)}</span>
                )}
                {property.roomCount && (
                  <span className="whitespace-nowrap">{formatRoomCount(property.roomCount)}</span>
                )}
              </div>
              {property.charges && (
                <span className="text-xs text-gray-500 mt-1">
                  {formatCharges(property.charges)}
                </span>
              )}
            </div>

            {/* Propriétaires */}
            <div className="flex items-center">
              {property.ownersCount > 0 && (
                <span className="text-xs text-gray-500">
                  {t("REAL_ESTATE_FORM.LABELS.OWNERS_COUNT", { count: property.ownersCount })}
                </span>
              )}
            </div>

            {/* Type de propriété et statuts */}
            <div className="flex flex-col items-end space-y-1">
              {property.propertyType && (
                <span className="inline-flex rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                  {property.propertyType}
                </span>
              )}
              {statusIndicators.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {statusIndicators.map((indicator, index) => (
                    <span
                      key={index}
                      className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600"
                    >
                      {indicator}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ChevronRightIcon
        className={`h-4 w-4 text-gray-400 group-data-[focus]:text-primary`}
      />
    </div>
  );
};
